import { CasinoConfig, getProductConfig, PRODUCT_CONFIG_KEYS } from '@fanduel/casino-config';
import type { FeatureFlags, ProductConfigKey } from '@fanduel/casino-config';
import type { Config } from '@fanduel/cpe-um-bundle/core-config/contract';
import { PRODUCT, BRAND, COUNTRY } from '@/utils/constants';

const productJsonKey = (): ProductConfigKey => {
    const product = process.env.PRODUCT;
    const brand = process.env.BRAND;
    const country = process.env.COUNTRY;

    if (product === PRODUCT.POKER) {
        return country === COUNTRY.CANADA ? PRODUCT_CONFIG_KEYS.pokerCA : PRODUCT_CONFIG_KEYS.pokerUS;
    }
    if (brand === BRAND.MOHEGANSUN) {
        return PRODUCT_CONFIG_KEYS.mohegansun;
    }
    if (country === COUNTRY.CANADA) {
        return PRODUCT_CONFIG_KEYS.casinoCA;
    }
    return PRODUCT_CONFIG_KEYS.casinoUS;
};

/**
 * Load and initialize CasinoConfig for the current product/environment/region.
 * This bridges to CoreConfig automatically (CasinoConfig calls CoreConfig.setConfig internally).
 *
 * Environment, region and client id are read from the CoreConfig payload (the
 * single source of truth) rather than re-derived from app config here.
 *
 * Must be called once at startup, before any UM modules that depend on CoreConfig.
 */
export const initializeCasinoConfig = async (coreConfig: Config): Promise<void> => {
    const key = productJsonKey();
    const json = getProductConfig(key);

    if (!json) {
        console.error(`[CasinoConfig] No product config found for key "${key}"`);
        return;
    }

    await CasinoConfig.loadConfig(json, coreConfig.environment.type, coreConfig.clientAuthToken);

    const region = coreConfig.appDomain.region;
    if (region) {
        await CasinoConfig.applyRegionChange(region.toUpperCase());
    }
};

/**
 * Re-apply region change (e.g. when user selects a new state).
 * Internally re-bridges to CoreConfig.
 */
export const refreshCasinoConfig = async (coreConfig: Config): Promise<void> => {
    const region = coreConfig.appDomain.region;
    if (region) {
        await CasinoConfig.applyRegionChange(region.toUpperCase());
    }
};

/**
 * Bridge CMS feature flags into CasinoConfig so UMs (e.g. casino-rewards-club)
 * can read them from the IoC-resolved config entries instead of via props.
 *
 * The host app calls this after feature flags are resolved from the CMS.
 * Converts the `Record<string, true>` CMS format to the CasinoConfig
 * `FeatureFlags` format (`Record<string, FeatureState>`).
 */
export const bridgeFeatureFlags = (cmsFlags: Record<string, true>): void => {
    const flags: FeatureFlags = Object.fromEntries(Object.keys(cmsFlags).map((key) => [key, 'enabled' as const]));
    CasinoConfig.setFeatureFlags(flags);
};
